import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api } from '../shell/bridge';
import type { ThreadMessage, LocalEcho } from '../hooks/useThread';
import type { Role } from '../lib/api';
import { SkeletonBlock } from '../ui/Skeleton';

interface ThreadProps {
  messages: ThreadMessage[];
  echoes: LocalEcho[];
  roleFor: (memberId: string | null | undefined) => Role | null;
  myMemberId: string | null;
  /** Second arg = the composer chip was armed (invoke path, card hphiEFLMogbg). */
  onSend: (text: string, wantsAgent?: boolean) => void;
  /** Skeleton while the first history page is in flight. */
  loading: boolean;
}

/**
 * The couple thread. Renders messages declaratively — React escapes text, so
 * the `escapeHtml` + `innerHTML` pair from main.ts is gone entirely.
 */
export function Thread({ messages, echoes, roleFor, myMemberId, onSend, loading }: ThreadProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState('');

  /* Invoke-path chip (card hphiEFLMogbg). Shown only when BOTH hold:
     - the couple chose `invoke` — an `auto` couple has no chip trigger
     - at least one AI permission is on, so a tap can actually succeed
     Two small GETs on mount; the server re-checks both regardless, so a stale
     value here can only waste a tap, never produce a wrong card. */
  const [showChip, setShowChip] = useState(false);
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [s, t] = await Promise.all([
          api<{ settings: { mode: string } }>('/api/ai/agent-settings'),
          api<{ toggles: Record<string, boolean> }>('/api/ai/toggles'),
        ]);
        if (!live) return;
        const anyOn = Object.values(t.toggles ?? {}).some(Boolean);
        setShowChip(s.settings?.mode === 'invoke' && anyOn);
      } catch (err) {
        console.error('[Pear] agent chip context failed', err);
        if (live) setShowChip(false);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const count = messages.length + echoes.length;
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    // One-shot: the chip arms exactly the next message, then disarms itself.
    onSend(text, armed && showChip);
    setArmed(false);
  };

  const hasContent = count > 0;

  return (
    <>
      <div id="thread-scroll" className="pear-thread" ref={scrollRef}>
        {loading && !hasContent && (
          <div style={{ padding: 'var(--space-4) 0' }}>
            <SkeletonBlock lines={3} />
          </div>
        )}

        {!loading && !hasContent && (
          <div className="pear-chat-empty" id="chat-empty">
            <p className="pear-chat-empty-text">Your thread will appear here when you join.</p>
          </div>
        )}

        {messages.map(msg =>
          msg.kind === 'agent-card' ? (
            <div className="pear-agent-card" key={`agent-${msg.seq}`} data-msg-seq={msg.seq}>
              <span className="pear-agent-dot" />
              <p className="pear-agent-copy">{msg.text}</p>
            </div>
          ) : (
            <Bubble
              key={msg.seq}
              text={msg.text}
              mine={msg.fromMemberId === myMemberId}
              role={roleFor(msg.fromMemberId)}
            />
          ),
        )}

        {echoes.map(echo => (
          <div className="pear-msg pear-msg-mine pear-local-echo" key={echo.clientMsgId} data-msg-id={echo.clientMsgId}>
            <div className="pear-msg-bubble pear-msg-bubble-b pear-msg-echo">
              {echo.text} <span className="pear-msg-sending">(sending…)</span>
            </div>
          </div>
        ))}
      </div>

      <div className="pear-chat-input-area">
        <form id="chat-form" className="pear-composer" onSubmit={submit}>
          <input
            type="text"
            id="chat-input"
            className="pear-chat-input pear-composer-input"
            placeholder="Type a reply…"
            aria-label="Message input"
            value={draft}
            onChange={e => setDraft(e.target.value)}
          />
          {showChip && (
            <button
              type="button"
              id="agent-chip"
              className={`pear-chip-agent${armed ? ' is-on' : ''}`}
              aria-pressed={armed}
              aria-label="Ask the agent to weigh in on this message"
              onClick={() => setArmed(v => !v)}
            >
              Agent
            </button>
          )}
          <button type="submit" className="pear-btn pear-btn-mustard pear-send" aria-label="Send message">
            →
          </button>
        </form>
      </div>
    </>
  );
}

/**
 * Two-ness: role A = bottle green, role B (or unknown, before members load) =
 * blush. Colours come from `--color-partner-a/b` instead of the hex literals
 * that lived in main.ts.
 */
function Bubble({ text, mine, role }: { text: string; mine: boolean; role: Role | null }) {
  const partnerA = role === 'A';
  return (
    <div
      className={`pear-msg ${mine ? 'pear-msg-mine' : 'pear-msg-other'}`}
      data-mine={String(mine)}
      data-role={role ?? ''}
    >
      <div className={`pear-msg-bubble ${partnerA ? 'pear-msg-bubble-a' : 'pear-msg-bubble-b'}`}>{text}</div>
    </div>
  );
}
